import React, { useState } from 'react';
import { User, Camera, Crown, Trash2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { ThemeToggle } from './ui/ThemeToggle';
import { SubscriptionCard } from './subscription/SubscriptionCard';
import { PricingModal } from './subscription/PricingModal';
import { getDisplayAvatarUrl, getProxyAvatarUrl } from '../utils/avatarUtils';
import { TimezoneSelector } from './ui/TimezoneSelector';
import { detectUserTimezone, normalizeTimezone } from '../utils/timezone';
import { ConfirmationModal } from './ui/ConfirmationModal';
import { DeleteAccountModal } from './profile/DeleteAccountModal';
import { AccountDeletionBanner } from './profile/AccountDeletionBanner';

interface UserProfileProps {
  onSuccess?: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ onSuccess }) => {
  const { user, updateProfile, isLoading } = useAuthStore();
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || '');
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [timeZone, setTimeZone] = useState<string>(normalizeTimezone(user?.timeZone || detectUserTimezone()));
  const [timeZoneDirty, setTimeZoneDirty] = useState<boolean>(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showTzConfirm, setShowTzConfirm] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

  if (!user) return null;

  const handleUpdateProfile = async () => {
    const updates: any = {};
    
    if (firstName !== user.firstName) updates.firstName = firstName;
    if (lastName !== user.lastName) updates.lastName = lastName;
    if (avatarUrl !== user.avatar) updates.avatar = avatarUrl;

    // For timezone, show confirmation first if changed
    if (timeZoneDirty && timeZone && timeZone !== user.timeZone) {
      setShowTzConfirm(true);
      // Defer actual update after confirmation
      return;
    }

    if (Object.keys(updates).length > 0) {
      const success = await updateProfile(updates);
      if (success && onSuccess) {
        onSuccess();
      }
    }
  };

  const confirmTimezoneChange = async () => {
    setShowTzConfirm(false);
    const updates: any = {};
    if (firstName !== user.firstName) updates.firstName = firstName;
    if (lastName !== user.lastName) updates.lastName = lastName;
    if (avatarUrl !== user.avatar) updates.avatar = avatarUrl;
    if (timeZoneDirty && timeZone && timeZone !== user.timeZone) updates.timeZone = timeZone;

    if (Object.keys(updates).length > 0) {
      const success = await updateProfile(updates);
      if (success && onSuccess) {
        onSuccess();
      }
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarUrl('');
    const success = await updateProfile({ avatar: '' });
    if (success && onSuccess) {
      onSuccess();
    }
  };

  return (
    <>
      <div className="p-6">
        {/* Title removed since modal provides it */}
        
        {/* Subscription Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Crown className="w-5 h-5 mr-2" />
            Subscription
          </h3>
          <SubscriptionCard 
            onUpgrade={() => setShowPricingModal(true)}
            onViewPlans={() => setShowPricingModal(true)}
          />
        </div>
        
        {/* Avatar Section */}
      <div className="flex flex-col items-center mb-6">
        <div className="relative">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
            {user.avatar ? (
              <img 
                src={getDisplayAvatarUrl(user, 80, false)} 
                alt={`${user.firstName} ${user.lastName}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Try proxy URL as fallback for Google images
                  const proxyUrl = getProxyAvatarUrl(user);
                  if (proxyUrl && e.currentTarget.src !== proxyUrl) {
                    e.currentTarget.src = proxyUrl;
                    return;
                  }
                  // Final fallback to User icon if proxy also fails
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <User className={`w-8 h-8 text-gray-600 ${user.avatar ? 'hidden' : ''}`} />
          </div>
          <button
            onClick={() => setAvatarUrl('')}
            className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600 transition-colors"
          >
            <Camera className="w-3 h-3" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">Profile Picture</p>
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        <Input
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Enter your first name"
        />
        
        <Input
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Enter your last name"
        />
        
        <Input
          label="Avatar URL"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
          placeholder="Enter image URL for your avatar"
          helperText="Paste a URL to an image you'd like to use as your profile picture"
        />

        {/* Timezone Selector */}
        <TimezoneSelector
          label="Timezone"
          helperText="Used to compute your habit day boundaries and streaks."
          value={timeZone}
          onChange={(tz) => { setTimeZone(tz); setTimeZoneDirty(true); }}
        />

        {/* Theme Settings */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Theme Preference
          </label>
          <ThemeToggle showLabel={true} />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Choose your preferred theme or follow your system setting
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        <Button
          onClick={handleUpdateProfile}
          isLoading={isLoading}
          className="flex-1"
        >
          Update Profile
        </Button>
        
        {user.avatar && (
          <Button
            variant="outline"
            onClick={handleRemoveAvatar}
            isLoading={isLoading}
          >
            Remove Avatar
          </Button>
        )}
      </div>

      {/* Account Deletion Banner */}
      <AccountDeletionBanner />

      {/* Account Deletion Section */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
          <Trash2 className="w-5 h-5 mr-2 text-red-500" />
          Danger Zone
        </h3>
        
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="font-medium text-red-800 dark:text-red-200 mb-1">
                Delete Account
              </h4>
              <p className="text-sm text-red-700 dark:text-red-300">
                Permanently delete your account and all associated data. This action cannot be undone, 
                but you'll have 30 days to change your mind.
              </p>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteAccountModal(true)}
              disabled={isLoading}
              className="ml-4 flex-shrink-0"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Pricing Modal */}
    <PricingModal
      isOpen={showPricingModal}
      onClose={() => setShowPricingModal(false)}
    />

    {/* Timezone Change Confirmation */}
    <ConfirmationModal
      isOpen={showTzConfirm}
      onClose={() => setShowTzConfirm(false)}
      onConfirm={confirmTimezoneChange}
      title="Confirm Timezone Change"
      message="Changing your timezone affects how we determine 'today' and may impact future streak calculations. This change applies going forward and will not recalculate past streaks. Proceed?"
      confirmText="Change Timezone"
      cancelText="Cancel"
    />

    {/* Delete Account Modal */}
    <DeleteAccountModal
      isOpen={showDeleteAccountModal}
      onClose={() => setShowDeleteAccountModal(false)}
    />
  </>
  );
}; 